/**
 * ScrollableImagePreview — a fixed SQUARE thumbnail (object-cover, anchored to
 * the top) that opens a dialog showing the image at full width inside a
 * vertically-scrollable frame. Long full-page screenshots stay compact in the
 * conversation and reveal their full length on click, instead of stretching the
 * thread into a tall strip. Mirrors the review dialog used by CapturedImageChip.
 */

import { useEffect, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

interface ScrollableImagePreviewProps {
  src: string
  alt?: string
  title?: string
  /** Overrides the square thumbnail sizing (default h-40 w-40). */
  thumbnailClassName?: string
}

export function ScrollableImagePreview({
  src,
  alt = '',
  title,
  thumbnailClassName,
}: ScrollableImagePreviewProps) {
  const [open, setOpen] = useState(false)
  const [failed, setFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Don't leave a blank box for an image that never loads (missing/expired key,
  // stuck request): after a grace period, fall back to the "unavailable" state.
  useEffect(() => {
    if (loaded || failed) return
    const t = setTimeout(() => setFailed(true), 15000)
    return () => clearTimeout(t)
  }, [loaded, failed])

  return (
    <>
      <button
        type="button"
        onClick={() => !failed && setOpen(true)}
        aria-label="Preview image"
        data-testid="thread-image-thumb"
        className={cn(
          'group relative block shrink-0 cursor-zoom-in overflow-hidden rounded-xl border border-border bg-background/60 transition-shadow hover:shadow-md',
          thumbnailClassName ?? 'h-40 w-40',
        )}
      >
        {failed ? (
          <div className="flex h-full w-full items-center justify-center bg-muted px-1 text-center text-[10px] text-muted-foreground">
            image unavailable
          </div>
        ) : (
          <>
            {!loaded && <div className="absolute inset-0 animate-pulse bg-muted" />}
            <img
              src={src}
              alt={alt}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              className={cn(
                'h-full w-full object-cover object-top transition-opacity group-hover:opacity-90',
                !loaded && 'opacity-0',
              )}
            />
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="truncate text-base font-normal">
              {title || alt || 'Image'}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-scroll rounded-md border border-border bg-background [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-foreground/50">
            <img src={src} alt={alt} className="w-full" />
          </div>
          <p className="text-xs text-muted-foreground">scroll to see the full page</p>
        </DialogContent>
      </Dialog>
    </>
  )
}
